import type {
  AcademicCourseDocument,
  AcademicCourseSchedule,
  AcademicSemesterConfiguration,
  AcademicTimetableDocument,
} from '../../types/sync';
import { parseTeachingWeeks } from '../courses/teaching-week';

export { parseTeachingWeeks } from '../courses/teaching-week';

export type HomeTimelineMode = 'day' | 'week' | 'month';

export interface HomeTimelineInterval {
  start: Date;
  end: Date;
}

export interface HomeTimelineRailItem {
  id: number;
  date: Date;
  primaryText?: string;
  secondaryText: string;
  isToday: boolean;
}

export interface HomeTimelineMarker {
  hasCourse: boolean;
  hasTodo: boolean;
}

export interface CourseOccurrence {
  id: string;
  course: AcademicCourseDocument;
  schedule: AcademicCourseSchedule;
  day: Date;
  startAt?: Date;
  endAt?: Date;
}

export function intervalContaining(date: Date, mode: HomeTimelineMode): HomeTimelineInterval {
  const start = mode === 'day'
    ? startOfDay(date)
    : mode === 'week'
      ? startOfWeek(date)
      : startOfMonth(date);
  const end = mode === 'day'
    ? addDays(start, 1)
    : mode === 'week'
      ? addDays(start, 7)
      : addMonths(start, 1);
  return { start, end };
}

export function shiftedDate(date: Date, mode: HomeTimelineMode, offset: number) {
  const normalized = intervalContaining(date, mode).start;
  if (mode === 'day') return addDays(normalized, offset);
  if (mode === 'week') return addDays(normalized, offset * 7);
  return addMonths(normalized, offset);
}

export function railItems(mode: HomeTimelineMode, referenceDate = new Date()) {
  const [lower, upper] = mode === 'day' ? [-90, 90] : mode === 'week' ? [-52, 52] : [-24, 24];
  const todayInterval = intervalContaining(referenceDate, mode);
  const items: HomeTimelineRailItem[] = [];
  for (let offset = lower; offset <= upper; offset += 1) {
    const date = shiftedDate(referenceDate, mode, offset);
    const range = intervalContaining(date, mode);
    const isToday = range.start <= referenceDate && referenceDate < range.end;
    if (mode === 'day') {
      items.push({
        id: offset,
        date,
        primaryText: String(date.getDate()),
        secondaryText: new Intl.DateTimeFormat('zh-CN', { weekday: 'short' }).format(date),
        isToday,
      });
    } else if (mode === 'week') {
      const end = addDays(range.end, -1);
      items.push({
        id: offset,
        date,
        secondaryText: `${date.getMonth() + 1}/${date.getDate()}–${end.getMonth() + 1}/${end.getDate()}`,
        isToday,
      });
    } else {
      items.push({
        id: offset,
        date,
        primaryText: `${date.getMonth() + 1}月`,
        secondaryText: String(date.getFullYear()),
        isToday,
      });
    }
  }
  return items;
}

export function dayRailMarkers(
  items: HomeTimelineRailItem[],
  courseDates: Date[],
  todoDates: Date[],
) {
  const courseDays = new Set(courseDates.map(dayKey));
  const todoDays = new Set(todoDates.map(dayKey));
  return new Map(items.flatMap(item => {
    const marker = {
      hasCourse: courseDays.has(dayKey(item.date)),
      hasTodo: todoDays.has(dayKey(item.date)),
    };
    return marker.hasCourse || marker.hasTodo ? [[item.id, marker] as const] : [];
  }));
}

export function courseOccurrences(
  courses: AcademicCourseDocument[],
  timetable: AcademicTimetableDocument | undefined,
  interval: HomeTimelineInterval,
) {
  const occurrences: CourseOccurrence[] = [];
  for (const course of courses) {
    if (course.isArchived || isQuickCourse(course)) continue;
    for (const schedule of course.schedules) {
      if (schedule.weekday < 1 || schedule.weekday > 7) continue;
      for (const day of datesInInterval(interval)) {
        if (mondayWeekday(day) !== schedule.weekday) continue;
        if (!isScheduleActive(course, schedule, day, timetable)) continue;
        const slotRange = occurrenceSlotRange(schedule, timetable);
        occurrences.push({
          id: `${schedule.id}:${dayKey(day)}`,
          course,
          schedule,
          day,
          startAt: slotRange ? dateAtMinutes(day, slotRange.startMinutes) : undefined,
          endAt: slotRange ? dateAtMinutes(day, slotRange.endMinutes) : undefined,
        });
      }
    }
  }
  return occurrences.sort((left, right) => {
    const time = (left.startAt?.getTime() ?? left.schedule.startSlot) -
      (right.startAt?.getTime() ?? right.schedule.startSlot);
    return time || left.course.name.localeCompare(right.course.name, 'zh-CN') ||
      left.schedule.id.localeCompare(right.schedule.id);
  });
}

function isScheduleActive(
  course: AcademicCourseDocument,
  schedule: AcademicCourseSchedule,
  date: Date,
  timetable?: AcademicTimetableDocument,
) {
  const semester = resolveSemester(course, schedule.weekPattern, timetable);
  if (!semester) return true;
  const week = teachingWeek(date, semester);
  return week !== undefined && parseTeachingWeeks(schedule.weekPattern, semester.totalWeeks).has(week);
}

function resolveSemester(
  course: AcademicCourseDocument,
  weekPattern: string,
  timetable?: AcademicTimetableDocument,
) {
  const identity = course.semesterAcademicYearStart && course.semesterTermRawValue
    ? `${course.semesterAcademicYearStart}-${course.semesterTermRawValue}`
    : undefined;
  const configurations = [timetable?.semester, ...(timetable?.semesterHistory ?? [])]
    .filter((value): value is AcademicSemesterConfiguration => Boolean(value));
  if (!identity) return timetable?.semester;
  const exact = configurations.find(value => semesterIdentity(value) === identity);
  if (exact) return exact;
  const maximumWeek = Math.max(20, ...[...weekPattern.matchAll(/\d+/g)].map(match => Number(match[0])));
  return standardSemester(
    course.semesterAcademicYearStart!,
    course.semesterTermRawValue!,
    Math.min(Math.max(maximumWeek, 20), 30),
  );
}

function occurrenceSlotRange(
  schedule: AcademicCourseSchedule,
  timetable?: AcademicTimetableDocument,
) {
  const slots = timetable?.slots ?? [];
  const start = slots[schedule.startSlot - 1];
  const end = slots[schedule.endSlot - 1];
  if (!start || !end || schedule.endSlot < schedule.startSlot) return undefined;
  return { startMinutes: start.startMinutes, endMinutes: end.endMinutes };
}

function teachingWeek(date: Date, semester: AcademicSemesterConfiguration) {
  const start = startOfDay(new Date(semester.firstWeekStart));
  const target = startOfDay(date);
  if (target < start) return undefined;
  const dayOffset = Math.round((target.getTime() - start.getTime()) / 86_400_000);
  const week = Math.floor(dayOffset / 7) + 1;
  return week >= 1 && week <= semester.totalWeeks ? week : undefined;
}

function standardSemester(academicYearStart: number, term: 1 | 2, totalWeeks: number) {
  const anchor = new Date(term === 1 ? academicYearStart : academicYearStart + 1, term === 1 ? 8 : 2, 1);
  const weekday = anchor.getDay();
  const daysUntilMonday = (8 - (weekday || 7)) % 7;
  return {
    academicYearStart,
    term,
    firstWeekStart: addDays(anchor, daysUntilMonday).toISOString(),
    totalWeeks,
  } satisfies AcademicSemesterConfiguration;
}

function semesterIdentity(value: AcademicSemesterConfiguration) {
  return `${value.academicYearStart}-${value.term}`;
}

function datesInInterval(interval: HomeTimelineInterval) {
  const values: Date[] = [];
  for (let day = startOfDay(interval.start); day < interval.end; day = addDays(day, 1)) {
    values.push(day);
  }
  return values;
}

export function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function startOfWeek(date: Date) {
  const day = startOfDay(date);
  return addDays(day, -(mondayWeekday(day) - 1));
}

export function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function addDays(date: Date, amount: number) {
  const value = new Date(date);
  value.setDate(value.getDate() + amount);
  return value;
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function mondayWeekday(date: Date) {
  const weekday = date.getDay();
  return weekday === 0 ? 7 : weekday;
}

function dayKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function dateAtMinutes(day: Date, minutes: number) {
  const date = startOfDay(day);
  date.setMinutes(minutes);
  return date;
}

function isQuickCourse(course: AcademicCourseDocument) {
  return course.id === '00000000-0000-4000-8000-000000000101';
}
